import { default as React } from 'react';
import { useState } from 'react';
import { BaseEditIcon } from '../BaseEditIcon';
import { DnsForwarderEditDialog } from './DnsForwarderEditDialog';
import { DomainForwarder } from '../types';
import { useAsync } from 'react-use';
import {
  alertApiRef,
  configApiRef,
  errorApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { WorkloadClient } from '@internal/workload-api-helper';
import { parseWorkloadObjectValues } from '../utils/workload';
import { getMessage } from '../../../utils/getMessage';
import { SeverityEnum } from '../../../enums/SeverityEnum';
import { isOkayResponse, isValueDefined } from '../utils/checks';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import _ from 'lodash';
import { useWorkload } from '../hooks/useWorkload';

let accountObject: { [key: string]: string | undefined };

interface DnsForwarderEditIconProps {
  domainForwardingRules: DomainForwarder;
  onDomainsUpdated?: (updatedDomains: string[]) => void;
}

// Main component
export const DnsForwarderEditIcon = ({
  domainForwardingRules,
  onDomainsUpdated,
}: DnsForwarderEditIconProps) => {
  const { entity } = useEntity();
  const { workload: workloadData, loading: workloadLoading } = useWorkload();
  const [dialogOpen, setDialogOpen] = useState(false);
  const identity = useApi(identityApiRef);
  const { value: profile } = useAsync(() => identity.getProfileInfo());
  const configApi = useApi(configApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  const workloadClient = new WorkloadClient(configApi, oauthApi);
  const alertApi = useApi(alertApiRef);
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const [loading, setLoading] = React.useState(false);
  const [updatedDomains, setUpdatedDomains] = useState<string[]>(
    domainForwardingRules.domains || [],
  );

  const stopLoadingAndCloseModal = () => {
    setDialogOpen(false);
  };

  const killProcessAndReportError = (e: any) => {
    stopLoadingAndCloseModal();
    errorApi.post(e);
  };

  const patchResourceData = async ({
    workload,
    resourceName,
    payload,
  }: {
    workload?: string;
    resourceName?: string;
    payload: { [_: string]: Object };
  }) => {
    return workloadClient.apiFetch(
      `workloads/workload/${workload}/resource/${resourceName}`,
      'PATCH',
      payload,
    );
  };

  const callPatchApi = async (updatedDnsForwarder: {}) => {
    const payload: { [key: string]: Object } = updatedDnsForwarder;
    const stripCatalogLocation = workloadData?.catalogLocation;
    const catalogLocationStringList = stripCatalogLocation?.split('/');
    if (catalogLocationStringList) {
      accountObject = parseWorkloadObjectValues(catalogLocationStringList);
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({
          failureToDo: 'find your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return Promise.resolve(false);
    }

    if (isValueDefined(accountObject?.workload)) {
      try {
        const patchResourceDataResponse = await patchResourceData({
          workload: accountObject.workload,
          resourceName: accountObject.resourceName,
          payload,
        });

        if (!isOkayResponse(patchResourceDataResponse)) {
          stopLoadingAndCloseModal();
          alertApi.post({
            message: getMessage({
              failureToDo: 'update your resource with the data provided',
            }).FAILURE,
            severity: SeverityEnum.ERROR,
          });
        }
      } catch (e: any) {
        killProcessAndReportError(e);
      }
    }
    await catalogApi.refreshEntity(stringifyEntityRef(entity));

    return Promise.resolve(true);
  };

  // Handlers for dialog actions
  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  // Handler for saving changes
  async function handleConfirm(e: any) {
    e.preventDefault();
    const updated: DomainForwarder = {
      ...domainForwardingRules,
      domains: updatedDomains,
    };

    if (!_.isEqual(domainForwardingRules, updated)) {
      setLoading(true);
      const config = { dns_forwarder: updated };
      await callPatchApi(config);

      // Notify parent component about the updated domains
      if (onDomainsUpdated) {
        onDomainsUpdated(updatedDomains);
      }
    }
    setLoading(false);
    domainForwardingRules.domains = updatedDomains;
    handleCloseDialog();
  }

  return (
    <BaseEditIcon
      workloadData={workloadData} // Pass workload data
      requesterEmail={profile?.email ?? ''} // Pass requester email
      handleClickOpen={handleOpenDialog} // Open dialog handler
      isModalOpen={dialogOpen} // Dialog open state
      handleClose={handleCloseDialog} // Close dialog handler
      handleConfirm={handleConfirm} // Confirm handler (empty for now)
      disableConfirm={_.isEqual(
        domainForwardingRules.domains ?? [],
        updatedDomains,
      )} // Disable confirm button until domains change
      isLoading={loading} // Loading state
      isLoadingEdit={workloadLoading} // Edit loading state
      dialogTitle="Add/Remove Domains" // Dialog title
      settingName="Internal DNS Resolution"
      allowEngineerEdit
      apiValidationPayload={domainForwardingRules.domains} // API validation payload
    >
      <DnsForwarderEditDialog
        tempDomains={updatedDomains}
        setTempDomains={setUpdatedDomains}
      />
    </BaseEditIcon>
  );
};
